import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PIService, RatingService } from '@/services/api';
import type { PrincipalInvestigator, Rating } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import RatingGrid from '@/components/RatingGrid';

const PIDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [pi, setPi] = useState<PrincipalInvestigator | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userRating, setUserRating] = useState<Rating | null>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  
  // Fetch PI details and user's existing rating
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch PI details
        const piData = await PIService.getById(id);
        setPi(piData);
        
        // If authenticated, fetch user's existing rating
        if (isAuthenticated) {
          try {
            const ratings = await RatingService.getUserRatings();
            const existingRating = ratings.find(
              (rating) => rating.principal_investigator === id
            );
            
            if (existingRating) {
              setUserRating(existingRating);
            }
          } catch (error) {
            console.error('Failed to fetch user ratings', error);
          }
        }
      } catch (err) {
        setError('Failed to load PI details. Please try again.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [id, isAuthenticated]);
  
  // Handle grid rating changes
  const handleRatingChange = (labManagementScore: number, interpersonalScore: number) => {
    setUserRating(prevRating => ({
      ...(prevRating || {}),
      principal_investigator: id || '',
      lab_management_score: labManagementScore,
      interpersonal_score: interpersonalScore,
    } as Rating));
    
    // Reset success message if user changes rating
    setSubmitSuccess(false);
  };
  
  // Handle rating submission
  const handleSubmitRating = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!userRating) return;
    
    setSubmitLoading(true);
    setError(null);
    
    try {
      // Submit or update rating
      if (userRating.id) {
        await RatingService.updateRating(userRating.id, {
          lab_management_score: userRating.lab_management_score,
          interpersonal_score: userRating.interpersonal_score,
          comment: userRating.comment,
        });
      } else {
        await RatingService.submitRating({
          principal_investigator: userRating.principal_investigator,
          lab_management_score: userRating.lab_management_score,
          interpersonal_score: userRating.interpersonal_score,
          comment: userRating.comment,
        });
      }
      
      setSubmitSuccess(true);
      
      // Refresh PI data to get updated scores
      if (id) {
        const piData = await PIService.getById(id);
        setPi(piData);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit rating. Please try again.');
      console.error(err);
    } finally {
      setSubmitLoading(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-neutral-600">Loading PI details...</p>
      </div>
    );
  }
  
  if (error || !pi) {
    return (
      <div className="max-w-4xl mx-auto bg-red-50 p-6 rounded-lg text-center">
        <h2 className="text-xl font-bold text-red-700 mb-2">Error</h2>
        <p className="text-red-600">{error || 'PI not found'}</p>
        <button
          onClick={() => navigate('/search')}
          className="mt-4 btn btn-outline"
        >
          Back to Search
        </button>
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto">
      {/* PI Header */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200 mb-8">
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">{pi.name}</h1>
        <div className="mb-4">
          <p className="text-xl text-neutral-600">{pi.institution_name}</p>
          {pi.research_field && (
            <p className="text-neutral-500">{pi.research_field}</p>
          )}
        </div>
        
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-neutral-50 p-3 rounded-lg">
            <div className="text-sm text-neutral-500">Egg Score</div>
            <div className="text-xl font-semibold">{Math.round(pi.avg_egg_score)}%</div>
          </div>
          
          <div className="bg-neutral-50 p-3 rounded-lg">
            <div className="text-sm text-neutral-500">Total Ratings</div>
            <div className="text-xl font-semibold">{pi.total_ratings}</div>
          </div>
          
          <div className="bg-neutral-50 p-3 rounded-lg">
            <div className="text-sm text-neutral-500">h-index</div>
            <div className="text-xl font-semibold">{pi.h_index || '-'}</div>
          </div>
        </div>
        
        {/* Score Distribution */}
        {pi.score_distribution && Object.keys(pi.score_distribution).length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-neutral-700 mb-2">Score Distribution</h3>
            <div className="flex h-4 rounded-full overflow-hidden">
              {Object.entries(pi.score_distribution).map(([range, percentage]) => (
                <div
                  key={range}
                  className="h-full bg-primary-500"
                  style={{
                    width: `${percentage}%`,
                    opacity: 0.3 + (parseInt(range.split('-')[1]) / 100) * 0.7
                  }}
                  title={`${range}: ${Math.round(Number(percentage))}%`}
                />
              ))}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-xs text-neutral-500">0</span>
              <span className="text-xs text-neutral-500">100</span>
            </div>
          </div>
        )}
        
        {pi.lab_website && (
          <div className="mt-4">
            <a
              href={pi.lab_website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondary-600 hover:text-secondary-700"
            >
              Visit Lab Website
            </a>
          </div>
        )}
      </div>
      
      {/* Rating Section */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200 mb-8">
        <h2 className="text-2xl font-bold mb-6">Rate This PI</h2>
        
        {!isAuthenticated ? (
          <div className="bg-primary-50 p-4 rounded-lg mb-6">
            <p className="text-neutral-700 mb-2">
              Please log in with your academic email to rate this PI.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="btn btn-primary"
            >
              Log In to Rate
            </button>
          </div>
        ) : (
          <>
            <RatingGrid
              onRatingChange={handleRatingChange}
              initialLabManagementScore={userRating?.lab_management_score}
              initialInterpersonalScore={userRating?.interpersonal_score}
            />
            
            {submitSuccess && (
              <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-4">
                Your rating has been submitted successfully.
              </div>
            )}
            
            <button
              onClick={handleSubmitRating}
              className="btn btn-primary w-full"
              disabled={submitLoading || !userRating}
            >
              {submitLoading
                ? 'Submitting...'
                : userRating?.id
                ? 'Update Rating'
                : 'Submit Rating'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PIDetail; 